import React, { useState, useMemo, useEffect } from 'react';

// Reusable list controls: text search + date-range filter + pagination.
// Designed to sit ON TOP of any status/tab filtering a page already does —
// pass in the already-status-filtered array as `items` and this hook adds
// search, date range and pagination on top without touching existing logic.
//
// searchFields: array of string keys or getter functions run against each item.
// dateField: string key or getter function returning a date-ish value (or null to disable).
export function useListControls(items, { searchFields = [], dateField = null, pageSize = 10 } = {}) {
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [search, dateFrom, dateTo, items.length]);

  const filtered = useMemo(() => {
    let list = items || [];
    if (search.trim() && searchFields.length) {
      const q = search.trim().toLowerCase();
      list = list.filter((item) =>
        searchFields.some((f) => {
          const val = typeof f === 'function' ? f(item) : item?.[f];
          return String(val || '').toLowerCase().includes(q);
        })
      );
    }
    if (dateField && (dateFrom || dateTo)) {
      list = list.filter((item) => {
        const raw = typeof dateField === 'function' ? dateField(item) : item?.[dateField];
        if (!raw) return false;
        const d = new Date(raw);
        if (isNaN(d.getTime())) return false;
        if (dateFrom && d < new Date(dateFrom)) return false;
        if (dateTo && d > new Date(dateTo + 'T23:59:59')) return false;
        return true;
      });
    }
    return list;
  }, [items, search, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginated = useMemo(
    () => filtered.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filtered, safePage, pageSize]
  );

  return {
    search, setSearch,
    dateFrom, setDateFrom,
    dateTo, setDateTo,
    page: safePage, setPage,
    pageSize,
    totalPages,
    filtered,
    paginated,
    totalCount: filtered.length,
  };
}

// Text search input + optional date range — matches existing .search-bar style.
export function ListToolbar({ search, setSearch, dateFrom, setDateFrom, dateTo, setDateTo, placeholder = 'Search...', showDateFilter = true, dateLabel = 'Date' }) {
  return (
    <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
      <div className="search-bar" style={{ maxWidth: 340 }}>
        <i className="bi bi-search" style={{ color: '#9ca3af' }} />
        <input placeholder={placeholder} value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {showDateFilter && (
        <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: '0.736rem' }}>
          <span style={{ color: '#9ca3af' }}><i className="bi bi-calendar3 me-1" />{dateLabel}</span>
          <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <span style={{ color: '#9ca3af' }}>to</span>
          <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          {(dateFrom || dateTo) && (
            <button className="btn btn-sm btn-light" title="Clear date filter" onClick={() => { setDateFrom(''); setDateTo(''); }}>
              <i className="bi bi-x-lg" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Pagination footer — "Showing X–Y of N" + page buttons (windowed with ellipses).
export function Pagination({ page, totalPages, setPage, totalCount, pageSize, itemLabel = 'records' }) {
  if (!totalCount) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);

  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || Math.abs(p - page) <= 1) pages.push(p);
    else if (pages[pages.length - 1] !== '...') pages.push('...');
  }

  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3 mb-2" style={{ fontSize: '0.736rem', color: '#6b7280' }}>
      <div>Showing {start}–{end} of {totalCount} {itemLabel}</div>
      {totalPages > 1 && (
        <div className="d-flex align-items-center gap-1">
          <button className="btn btn-sm btn-light" disabled={page <= 1} onClick={() => setPage(Math.max(1, page - 1))}>
            <i className="bi bi-chevron-left" />
          </button>
          {pages.map((p, i) =>
            p === '...' ? (
              <span key={`e${i}`} className="px-1">…</span>
            ) : (
              <button
                key={p}
                className="btn btn-sm"
                style={p === page ? { background: 'linear-gradient(135deg,#1a5276,#1e8449)', color: '#fff', border: 'none' } : { background: '#f3f4f6', color: '#374151', border: 'none' }}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            )
          )}
          <button className="btn btn-sm btn-light" disabled={page >= totalPages} onClick={() => setPage(Math.min(totalPages, page + 1))}>
            <i className="bi bi-chevron-right" />
          </button>
        </div>
      )}
    </div>
  );
}
